// /connect — Connections: where Assertion is running for you. A STATUS BOARD first, an installer
// second. Deliberately not two symmetric columns selling the agent against the plugin: a page that
// asks you to pick a product on every visit reads as "did I choose wrong?". So it shows what you
// actually run — each client with its version, when it was last seen and whether it is behind — and
// mentions the other product in one line you can ignore. Someone with nothing connected sees the
// plugin's install, which signs in from the browser (/assertion:login, no key to copy), with the agent
// a line below. The key itself sits in a collapsed "Use a key instead" section, for CI and shared machines.
// Signing in (full-page, returns to this page — authConfig.js signInWithRedirect) provisions the account + org server-side
// (GET /me/api-key). Standalone, public, outside the analytics app shell.
import { useCallback, useEffect, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { Link as RouterLink } from "react-router-dom";
import {
  Box, Button, Typography, Paper, Tabs, Tab, IconButton, CircularProgress,
  Snackbar, Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CodeBlock from "../components/CodeBlock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import { signInWithRedirect } from "../authConfig";
import { getMyApiKey, rotateMyApiKey, getAgentHome } from "../utils/api";
import { AGENT_SITE } from "../utils/landing";
import MemoryNav from "../components/MemoryNav";
import { siteInstallUrl, wantsSignIn, SITE_INSTALL } from "../utils/siteInstall";

// The on-ramp: the same memory inside someone else's agent. Since plugin 0.3.8 each one signs in from
// the browser (click Connect), so the main install never shows a key. `keyInstall` is the same install
// with a key, for CI and shared machines, shown only inside "Use a key instead".
const PLUGINS = [
  {
    key: "claude",
    label: "Claude Code",
    steps: [
      "In Claude Code, run these four commands. The last one opens your browser: click Connect, and memory is on in the same session. No key to copy, no restart.",
      "Prefer the terminal? Run claude plugin marketplace add Assertion-AI/assertion && claude plugin install assertion@assertion-ai before you start Claude Code, then run /assertion:login.",
    ],
    install: () =>
      `/plugin marketplace add Assertion-AI/assertion\n` +
      `/plugin install assertion@assertion-ai\n` +
      `/reload-plugins\n` +
      `/assertion:login`,
    keyInstall: (key) =>
      `# 1) install the plugin (in Claude Code)\n` +
      `/plugin marketplace add Assertion-AI/assertion\n` +
      `/plugin install assertion@assertion-ai\n\n` +
      `# 2. add your key to ~/.claude/settings.json\n` +
      `{\n  "env": { "ASSERTION_API_KEY": "${key}" }\n}`,
    update: () =>
      `/assertion:upgrade\n` +
      `/reload-plugins`,
    after: "Installing inside a session that's already open? /reload-plugins turns the plugin on without a restart. The same goes for updates: /assertion:upgrade, then /reload-plugins.",
  },
  {
    key: "codex",
    label: "Codex",
    // Deliberately comment-free. In an interactive zsh — the macOS default, and
    // where this gets pasted — INTERACTIVE_COMMENTS is off, so '#' is not a
    // comment. A '#' line containing ')' (as "# 1) install…" did) is a hard parse
    // error, and because a bracketed paste is parsed as ONE buffer, that error
    // aborts the entire block: the user sees one "parse error near ')'" and
    // nothing at all is installed. Setting the option inside the block cannot fix
    // it either, since the whole buffer is parsed before its first line runs.
    // The explanation lives in `steps` below, rendered outside the copyable text.
    install: () =>
      `git clone https://github.com/Assertion-AI/assertion\n` +
      `python3 assertion/plugin/codex/install_codex.py\n` +
      `export PATH="$HOME/.local/bin:$PATH"\n` +
      `codex`,
    keyInstall: (key) =>
      `codex plugin marketplace add Assertion-AI/assertion\n` +
      `codex plugin add assertion@assertion-ai\n` +
      `mkdir -p ~/.assertion ~/.codex\n` +
      `echo '{"api_key":"${key}"}' > ~/.assertion/credentials.json\n` +
      `chmod 600 ~/.assertion/credentials.json\n` +
      `grep -qF '[mcp_servers.assertion]' ~/.codex/config.toml 2>/dev/null || cat >> ~/.codex/config.toml <<'EOF'\n\n` +
      `[mcp_servers.assertion]\n` +
      `url = "https://memory.assertion-ai.com/memory/mcp/default"\n` +
      `http_headers = { Authorization = "Bearer ${key}" }\n` +
      `EOF\n` +
      `export PATH="$HOME/.local/bin:$PATH"\n` +
      `codex`,
    // Instructions, not a description of the script. Two actions, in order, and nothing else to decide.
    steps: [
      "Copy the whole block below, paste it into a terminal, and press Enter. It installs the plugin and opens your browser to sign in: click Connect. No key to copy.",
      "Codex opens by itself at the end. When it asks you to review the hooks, choose Trust all and continue, then quit. One time only, and you're done.",
    ],
    update: () =>
      `# refresh the marketplace snapshot, then re-add to get the latest\n` +
      `codex plugin marketplace upgrade assertion-ai\n` +
      `codex plugin add assertion@assertion-ai`,
  },
  {
    key: "cursor",
    label: "Cursor",
    steps: [
      "Run this in a terminal. It installs the hooks and memory tools, and opens your browser to sign in: click Connect. No key to copy.",
      "Fully quit and reopen Cursor. It loads new hooks and memory tools only at launch.",
    ],
    install: () =>
      `git clone https://github.com/Assertion-AI/assertion\n` +
      `cd assertion/plugin/cursor && python3 install_cursor.py`,
    keyInstall: (key) =>
      `git clone https://github.com/Assertion-AI/assertion\n` +
      `cd assertion/plugin/cursor && python3 install_cursor.py --key ${key}`,
    update: () =>
      `# in Cursor, run /upgrade — or, in the folder where you cloned 'assertion':\n` +
      `cd assertion && git pull\n\n` +
      `# (no need to re-run install_cursor.py — hooks run from the clone)`,
    after: "After installing or updating, fully quit and reopen Cursor, then send one message. Cursor loads new hooks and memory tools only at launch.",
  },
];

// The backend stamps a client per install: "claude", "cursor", "codex" for the plugin, and the
// agent's own names. Instance suffixes ("claude:a3f9") are normalized away.
// Inside the agent's own panel a row names the INSTALL, not the product ("Terminal", "Mac app") —
// repeating "Assertion agent" under a heading that already says it tells the reader nothing.
const CLIENT_NAMES = { claude: "Claude Code", cursor: "Cursor", codex: "Codex",
                       opencode: "Terminal", assertion: "Terminal", agent: "Terminal",
                       "assertion-desktop": "Mac app", desktop: "Mac app" };
const baseName = (c) => String(c || "").split(":")[0].toLowerCase();
const DORMANT_MS = 7 * 24 * 3600 * 1000;

function relativeTime(iso) {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return "";
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return d < 30 ? `${d} d ago` : `${Math.round(d / 30)} mo ago`;
}

// One install, as a line: what it is, what it runs, and whether it needs anything from you.
function ClientRow({ row, onUpdate }) {
  const base = baseName(row.client);
  const t = row.at ? Date.parse(row.at) : NaN;
  const dormant = Number.isFinite(t) && Date.now() - t > DORMANT_MS;
  const seen = relativeTime(row.at);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25, flexWrap: "wrap",
               borderBottom: "1px solid #21212a" }}>
      <Typography sx={{ flex: 1, minWidth: 140, color: "#e3e5ea", fontSize: 13.5, fontWeight: 600 }}>
        {CLIENT_NAMES[base] || base}
        {row.version ? <Box component="span" sx={{ ml: 1, color: "#7d818b", fontWeight: 400 }}>v{row.version}</Box> : null}
      </Typography>
      {row.outdated ? (
        onUpdate ? (
          <Button size="small" onClick={onUpdate}
                  sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: "#d9a93a", minWidth: 0, px: 1 }}>
            Update available →
          </Button>
        ) : (
          <Box component="a" href={AGENT_SITE} sx={{ fontSize: 12, fontWeight: 700, color: "#d9a93a", textDecoration: "none" }}>
            Update available →
          </Box>
        )
      ) : (
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#2da894" }}>Current</Typography>
      )}
      <Typography sx={{ fontSize: 12, color: dormant ? "#6b6f78" : "#9aa0aa", minWidth: 96, textAlign: "right" }}>
        {seen ? (dormant ? `${seen} · idle` : seen) : "not seen yet"}
      </Typography>
    </Box>
  );
}

function mask(key) {
  if (!key || key.length < 8) return "••••••••";
  return `${key.slice(0, 4)}${"•".repeat(Math.max(8, key.length - 8))}${key.slice(-4)}`;
}

const PANEL = { width: "100%", maxWidth: 720, p: { xs: 3, md: 4 },
                bgcolor: "#15151c", border: "1px solid #26262f", borderRadius: 3 };
const STEP_TITLE = { color: "#e3e5ea", fontSize: 13.5, fontWeight: 700, mb: 1 };
const STEPS = { m: 0, mb: 1.5, pl: 2.5, color: "#aeb3bc", fontSize: 12.5, lineHeight: 1.7, "& li": { mb: 0.25 } };

export default function Plugin() {
  const { instance, inProgress } = useMsal();
  const isAuth = useIsAuthenticated();
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reveal, setReveal] = useState(false);
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState("");
  // The status board. Read-only and entirely optional: if this call fails, the page still does its
  // other job (key + install), which is the one a new user came for.
  const [home, setHome] = useState(null);

  const fetchKey = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { api_key } = await getMyApiKey();
      setApiKey(api_key);
    } catch (e) {
      if (e?.message === "REDIRECTING_FOR_AUTH") return;
      // The first call for a brand-new user provisions their account server-side;
      // that write completes even if we time out, so a single retry hits the fast
      // path and returns immediately.
      try {
        const { api_key } = await getMyApiKey();
        setApiKey(api_key);
      } catch (e2) {
        if (e2?.message !== "REDIRECTING_FOR_AUTH") {
          setError(e2?.response?.data?.error || e2?.message || "Could not load your key.");
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let dead = false;
    if (isAuth && inProgress === InteractionStatus.None) {
      getAgentHome().then((d) => { if (!dead) setHome(d); }).catch(() => {});
    }
    return () => { dead = true; };
  }, [isAuth, inProgress]);

  // Once signed in, fetch the key automatically — it's only the plugin section that needs it.
  useEffect(() => {
    if (isAuth && inProgress === InteractionStatus.None && !apiKey) fetchKey();
  }, [isAuth, inProgress, apiKey, fetchKey]);

  const signIn = async () => {
    setError("");
    try {
      await signInWithRedirect(instance);   // full page, back here after (see authConfig.js)
    } catch (e) {
      setError(e?.message || "Sign-in failed. Please try again.");
    }
  };

  const rotate = async () => {
    if (!window.confirm("Rotate your key? The old key stops working immediately.")) return;
    setLoading(true);
    try {
      const { api_key } = await rotateMyApiKey();
      setApiKey(api_key);
      setReveal(true);
      setToast("Key rotated — update your install with the new key.");
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Could not rotate key.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast(`${what} copied`);
    } catch {
      setToast("Copy failed — select and copy manually.");
    }
  };

  useEffect(() => {
    document.title = "Plugin · Assertion AI Studio";
    return () => { document.title = "Assertion AI Studio"; };
  }, []);

  // Signed out and not here for a key: the site's install pages are the place (see utils/siteInstall).
  const forKey = wantsSignIn(window.location.search);
  const toSite = !isAuth && !forKey;
  useEffect(() => {
    if (toSite && inProgress === InteractionStatus.None) window.location.replace(siteInstallUrl(window.location.search));
  }, [toSite, inProgress]);

  const busy = loading || inProgress !== InteractionStatus.None;
  const plugin = PLUGINS[tab];
  const pluginClients = home?.plugin_clients || [];
  const agentClients = home?.clients || [];
  const nothingConnected = Boolean(home) && pluginClients.length === 0 && agentClients.length === 0;
  // A stale client's "Update available" jumps to that client's own update command below.
  const showUpdateFor = (clientName) => {
    const i = PLUGINS.findIndex((x) => x.key === baseName(clientName));
    if (i >= 0) setTab(i);
  };

  // Leaving for the site: paint the page's ground, not a sign-in wall that flashes and vanishes.
  if (toSite) return <Box sx={{ minHeight: "100vh", bgcolor: "#0b0b0f" }} data-testid="to-site-install" />;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#0b0b0f", color: "#fff", display: "flex", flexDirection: "column" }}>
      <MemoryNav />
      <Box flex={1} display="flex" flexDirection="column" alignItems="center"
           sx={{ px: 2, py: { xs: 4, md: 8 } }}>
      {/* The board below says what is running, whether it is current and when it last reported, so a
          tagline under the title would only say it again in worse words. The one reader who needs a
          sentence is the one with nothing connected, who cannot see a board at all. */}
      <Typography variant="h4" fontWeight={700} textAlign="center" sx={{ mb: nothingConnected ? 1.5 : 4 }}>
        Connections
      </Typography>
      {nothingConnected && (
        <Typography variant="body2" sx={{ opacity: 0.7, mb: 4, textAlign: "center", maxWidth: 520 }}>
          Nothing is connected yet. Install the free plugin below to keep the agent you already use, or
          download Assertion's own agent.
        </Typography>
      )}

      <Paper elevation={0} sx={PANEL} data-testid="plugin-panel">
        {!isAuth ? (
          <Box textAlign="center">
            <Button variant="contained" size="large" onClick={signIn} disabled={busy}>
              {busy ? <CircularProgress size={22} /> : "Sign in"}
            </Button>
            <Typography variant="caption" display="block" sx={{ color: "#7d818b", mt: 2 }}>
              Sign in to see your key and the computers you've connected.
            </Typography>
            <Typography variant="body2" sx={{ color: "#aeb3bc", mt: 3 }}>
              New here?{" "}
              <Box component="a" href={SITE_INSTALL} sx={{ color: "#5cc4ae", fontWeight: 600 }}>
                Install free →
              </Box>
            </Typography>
          </Box>
        ) : loading && !apiKey ? (
          <Box textAlign="center" py={3}><CircularProgress /></Box>
        ) : apiKey ? (
          <>
            {pluginClients.length > 0 && (
              <Box sx={{ mb: 3 }} data-testid="plugin-board">
                <Typography variant="overline" sx={{ color: "#9aa0aa" }}>Your plugin</Typography>
                <Box sx={{ mt: 0.5, borderTop: "1px solid #21212a" }}>
                  {pluginClients.map((c) => (
                    <ClientRow key={c.client} row={c} onUpdate={() => showUpdateFor(c.client)} />
                  ))}
                </Box>
                <Typography variant="caption" display="block" sx={{ color: "#7d818b", mt: 1 }}>
                  Latest v{home?.latest_plugin_version || "—"}. A version registers on the first turn after a fresh launch.
                </Typography>
              </Box>
            )}

            {/* Set up per client — first-time install, then update */}
            <Typography variant="overline" sx={{ color: "#9aa0aa" }}>
              {pluginClients.length > 0 ? "Add another client" : "Set up"}
            </Typography>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2,
                  "& .MuiTab-root": { color: "#888", textTransform: "none" },
                  "& .Mui-selected": { color: "#fff !important" } }}>
              {PLUGINS.map((p) => <Tab key={p.label} label={p.label} />)}
            </Tabs>

            <Typography sx={STEP_TITLE}>First-time install</Typography>
            {plugin.steps && (
              <Box component="ol" sx={STEPS}>
                {plugin.steps.map((s, i) => <li key={i}>{s}</li>)}
              </Box>
            )}
            <CodeBlock text={plugin.install()} copyLabel="install command" onCopy={copy} />

            <Typography sx={{ ...STEP_TITLE, mt: 3 }}>Already installed? Update to the latest</Typography>
            <CodeBlock text={plugin.update(apiKey)} copyLabel="update command" onCopy={copy} />

            <Box sx={{ mt: 2, p: 1.5, bgcolor: "#0e0e14", border: "1px solid #26262f",
                 borderLeft: "3px solid #2da894", borderRadius: 1.5 }}>
              {plugin.after && (
                <Typography variant="caption" display="block" sx={{ color: "#aeb3bc", lineHeight: 1.6 }}>
                  {plugin.after}
                </Typography>
              )}
              {plugin.label === "Codex" && (
                <Typography variant="caption" display="block" sx={{ color: "#aeb3bc", lineHeight: 1.6 }}>
                  <strong>Codex:</strong> the trust step has to happen in a terminal — the desktop app has
                  no trust dialog. Already in a Codex session? Run <code>/hooks</code>, press <code>t</code>,
                  then <code>/new</code>. Doing it once is enough: trust is saved to <code>~/.codex/config.toml</code>,
                  which covers the terminal, the desktop app and the VS Code extension. If you use the
                  desktop app, quit it fully (<code>⌘Q</code>) and reopen afterwards — it only reads that
                  file at launch. And don't check <strong>Settings → Hooks</strong> to see whether it
                  worked; it can say <em>“No hooks found”</em> while capture is running perfectly. Ask your
                  agent about past work only your memory would know instead.
                </Typography>
              )}
            </Box>

            <Box component="details" data-testid="key-section"
                 sx={{ mt: 3, "& > summary": { cursor: "pointer", color: "#9aa0aa", fontSize: 12.5, fontWeight: 600,
                       mb: 1.5, "&:focus-visible": { outline: "2px solid #2da894", outlineOffset: 2 } } }}>
              <summary>Use a key instead (CI or a shared machine)</summary>
              {/* Key row */}
            <Typography variant="overline" sx={{ color: "#9aa0aa" }}>Your API key</Typography>
            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 3,
                 fontFamily: "monospace", bgcolor: "#0b0b0f", border: "1px solid #26262f",
                 borderRadius: 2, px: 2, py: 1.25 }}>
              <Box flex={1} sx={{ overflowX: "auto", whiteSpace: "nowrap", color: "#fff", fontSize: 14, letterSpacing: 0.5 }}>
                {reveal ? apiKey : mask(apiKey)}
              </Box>
              <Tooltip title={reveal ? "Hide" : "Reveal"}>
                <IconButton size="small" sx={{ color: "#aaa" }} onClick={() => setReveal((v) => !v)}>
                  {reveal ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy key">
                <IconButton size="small" sx={{ color: "#aaa" }} onClick={() => copy(apiKey, "Key")}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rotate key">
                <IconButton size="small" sx={{ color: "#aaa" }} onClick={rotate} disabled={busy}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

              <Typography sx={STEP_TITLE}>Install with a key</Typography>
              <CodeBlock text={plugin.keyInstall(apiKey)} copyLabel="install command with key" onCopy={copy} />
              <Typography variant="caption" display="block" sx={{ color: "#7d818b", mt: 1.5 }}>
                A key set this way takes precedence over the sign-in. Treat it like a password — anyone
                with it can write to your memory.
              </Typography>
            </Box>
            <Typography variant="caption" display="block" sx={{ color: "#7d818b", mt: 2 }}>
              Once it's captured something, open <strong>Memory</strong> above to browse your spaces.
            </Typography>
          </>
        ) : null}

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>{error}</Typography>
        )}
      </Paper>

      {isAuth && (
        <Paper elevation={0} sx={{ ...PANEL, mt: 2.5 }} data-testid="agent-panel">
          <Typography variant="overline" sx={{ color: "#9aa0aa" }}>Assertion agent</Typography>
          {agentClients.length > 0 ? (
            <>
              <Box sx={{ mt: 0.5, borderTop: "1px solid #21212a" }}>
                {agentClients.map((c) => <ClientRow key={c.client} row={c} />)}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                         gap: 2, mt: 1.5, flexWrap: "wrap" }}>
                <Typography variant="caption" sx={{ color: "#7d818b" }}>
                  Latest v{home?.latest_version || "—"}. Your plan and usage are under{" "}
                  <Box component={RouterLink} to="/account" sx={{ color: "#2da894", fontWeight: 600 }}>Account</Box>.
                </Typography>
                <Button size="small" variant="outlined" component="a" href={AGENT_SITE}
                        sx={{ textTransform: "none", color: "#2da894", borderColor: "#2da894", whiteSpace: "nowrap" }}>
                  Install on another machine
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                       gap: 2, mt: 1, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ color: "#aeb3bc", maxWidth: 440, lineHeight: 1.6 }}>
                Assertion's own agent keeps context flat as a session runs, with memory native from the
                first prompt. It runs on a plan; the plugin does not.
              </Typography>
              <Button size="small" variant="outlined" component="a" href={AGENT_SITE}
                      sx={{ textTransform: "none", color: "#2da894", borderColor: "#2da894", whiteSpace: "nowrap" }}>
                Download the agent
              </Button>
            </Box>
          )}
        </Paper>
      )}

      </Box>
      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast("")}
                message={toast} anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </Box>
  );
}
